import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, HelpCircle, Zap, Shield, DollarSign, Mail } from "lucide-react";
import DiscordLogo from "@/assets/discord-logo.jpg";

function HelpSection({ icon: Icon, title, children, color }) {
  return (
    <div className="rounded-xl border border-border/60 bg-background p-4">
      <div className="flex items-center gap-2.5">
        <div className={`rounded-lg p-1.5 ${color || "bg-violet-500/10"}`}>
          <Icon size={16} className="text-current" />
        </div>
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      <div className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </div>
  );
}

export function HelpPage() {
  return (
    <div data-testid="help-page" className="space-y-6">
      <div>
        <h1 data-testid="help-title" className="page-title">Help & FAQ</h1>
        <p data-testid="help-subtitle" className="page-subtitle">
          Everything you need to know about using ClippingBase.
        </p>
      </div>

      <Card data-testid="help-card" className="border-border/60 bg-card/80">
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Quick Start Guide</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <HelpSection icon={Zap} title="Getting Started" color="bg-amber-500/10 text-amber-500">
            <p>1. Browse <strong>Campaigns</strong> and join one that fits your content style.</p>
            <p>2. Click on a campaign and use the <strong>Submit</strong> tab to send your clip link.</p>
            <p>3. Track your clip status and views in the <strong>Dashboard</strong>.</p>
          </HelpSection>

          <HelpSection icon={DollarSign} title="How Payouts Work" color="bg-emerald-500/10 text-emerald-500">
            <p>Earnings are based on <strong>CPM</strong> (cost per 1,000 views) set by each campaign.</p>
            <p>Views are synced automatically for TikTok and YouTube. Instagram views are entered manually by admins.</p>
            <p>Once your pending balance is approved, it moves to <strong>Available</strong> for withdrawal.</p>
          </HelpSection>

          <HelpSection icon={Shield} title="Social Verification" color="bg-cyan-500/10 text-cyan-500">
            <p>Add your social accounts in <strong>Settings</strong>.</p>
            <p>You'll receive a unique code to add to your bio.</p>
            <p>Click <strong>Verify</strong> — we'll check your bio automatically to confirm ownership.</p>
          </HelpSection>

          <HelpSection icon={HelpCircle} title="Tips" color="bg-violet-500/10 text-violet-500">
            <p>Submit clips as soon as they're posted for fastest view tracking.</p>
            <p>Keep your payment methods up to date in the <strong>Wallet</strong> page.</p>
            <p>Check the <strong>Leaderboard</strong> tab in each campaign to see how you rank.</p>
          </HelpSection>
        </CardContent>
      </Card>



      <Card data-testid="help-discord" className="border-border/60 bg-card/80">
        <CardContent className="flex flex-col items-center gap-4 py-8 text-center sm:flex-row sm:text-left">
          <div className="rounded-2xl bg-[#5865F2]/10 p-4">
            <img src={DiscordLogo} alt="Discord" className="h-7 w-7" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold">Need more help?</div>
            <div className="mt-1 text-sm text-muted-foreground">
              Join our Discord community for direct support, announcements, and tips from other clippers.
            </div>
          </div>
          <Button
            data-testid="help-discord-link"
            className="gap-2 rounded-full bg-[#5865F2] text-white hover:bg-[#4752C4]"
            onClick={() => window.open("https://discord.gg/UPUrB8EKbT", "_blank")}
          >
            <img src={DiscordLogo} alt="Discord" className="h-4 w-4 rounded-sm" />
            Join Discord <ExternalLink size={14} />
          </Button>
        </CardContent>
      </Card>

      <Card data-testid="help-support-email-card" className="border-[#c8ff00]/20 bg-[#c8ff00]/[0.03]">
        <CardContent className="flex flex-col items-center gap-4 py-6 text-center sm:flex-row sm:text-left">
          <div className="rounded-2xl bg-[#c8ff00]/10 p-4"><Mail size={24} className="text-[#c8ff00]" /></div>
          <div className="flex-1"><div className="text-sm font-semibold">Support Email</div><a data-testid="help-support-email" href="mailto:hello@clippingbase.com" className="mt-1 block text-sm font-semibold text-[#c8ff00] hover:text-[#d8ff33]">hello@clippingbase.com</a></div>
        </CardContent>
      </Card>
    </div>
  );
}
